import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Paper } from "@mui/material";
import { Search } from "@mui/icons-material";
import { GhostType, KGPLocation, ResourceBookingType, SpBookingType } from "../KGP";
import KGPResourceOrder from "./KGPResourceOrder";
import KGPResource from "./KGPResource";
import { useDrop } from "react-dnd";
import ApiService from "@/api/ApiService";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { useCallback, useEffect, useState } from "react";

export default function KGPContainer({
  x,
  searchString,
  date,
  sameColor,
  transferredNumbers,
  disabled,
  setJobInfoId,
}: {
  sameColor: string;
  searchString: string;
  x: KGPLocation;
  transferredNumbers: Set<string>;
  date: string;
  disabled: boolean;
  setJobInfoId: (id: number) => void;
}) {
  const [jobSelectorOpen, setJobSelectorOpen] = useState(false);
  useEffect(() => setJobSelectorOpen(false), [x.id, date]);
  const jobs = x.type === "project" && x.hasPlanning ? [...(x.jobs ?? [])].sort((a, b) => a.phase.name.localeCompare(b.phase.name) || a.id - b.id) : [];

  const queryClient = useQueryClient();

  const changeMutation = useMutation<any, AxiosResponse, ChangeData>({
    mutationFn: ApiService.changeKGPCalendar(),
    onSettled: (answer) => {
      queryClient.invalidateQueries({ queryKey: ["getCalendar"] });
      queryClient.invalidateQueries({ queryKey: ["getKGP"] });
      queryClient.invalidateQueries({ queryKey: ["getTransfers"] });
      queryClient.invalidateQueries({ queryKey: ["getOpenRequests"] });
    },
  });

  const [{ isOver }, drop] = useDrop<ResourceBookingType | SpBookingType | GhostType, void, { isOver: boolean }>(
    () => ({
      accept: ["ghost", "spBooking", "kgBooking"],
      collect: (monitor) => {
        return {
          isOver: !!monitor.isOver({ shallow: true }),
        };
      },
      drop: (item, monitor) => {
        if (monitor.didDrop()) return;
        if (x.type === "specialPlace") {
          changeMutation.mutate({
            delete: item.type === "kgBooking" || item.type === "spBooking" ? { type: item.type, id: item.id } : null,
            create: {
              type: "spBooking",
              resource: item.resource.id,
              target: x.id,
              date,
            },
          });
        } else if (x.type === "project") {
          console.log(item, x);
          changeMutation.mutate({
            delete: item.type === "kgBooking" || item.type === "spBooking" ? { type: item.type, id: item.id } : null,
            create: {
              type: "kgBooking",
              resource: item.resource.id,
              erfasser: x.erfasser.id,
              project: x.project.id,
              date,
            },
          });
        }
      },
    }),
    [x],
  );

  const search = useCallback(
    (terms: string[]) => {
      if (!searchString) return true;
      const searchTerms = searchString
        .split(" ")
        .map((x) => x.toLocaleLowerCase().trim().replace(/_/g, " "))
        .filter((x) => !!x.length);
      if (!searchTerms.length) return true;
      for (let term of searchTerms) {
        let found = false;
        for (let headCell of terms) {
          if (headCell.toLocaleLowerCase().includes(term)) {
            found = true;
            break;
          }
        }
        if (!found) return false;
      }
      return true;
    },
    [searchString],
  );

  const crew = x.type === "project" ? x.erfasser.name : null;

  const nojobs = x.type === "project" && !x.hasPlanning;
  return (
    <Paper
      ref={x.type === "none" ? undefined : drop}
      className={
        "kgpbox " +
        x.type +
        (nojobs ? " ghost" : "") +
        (isOver ? " dropover" : "") +
        (" kgpboxspan" + Math.min(14, Math.ceil(x.content.length / 4))) +
        (searchString.length ? " searchActive" : "")
      }
    >
      <div className="kgptitle" style={sameColor !== "" ? { borderRight: "15px solid " + sameColor } : {}}>
        {x.type === "project" ? <div className="kgptitlenumber">{x.project.number}</div> : null}
        <div className="kgptitlename">{x.type === "project" ? x.project.name : x.name}</div>
        {jobs.length > 0 ? <IconButton
          aria-label="Projektinformationen öffnen"
          title="Projektinformationen öffnen"
          sx={{ p: 0, width: 14, height: 14, flexShrink: 0, color: "inherit" }}
          onPointerDown={(event) => event.stopPropagation()}
          onClick={(event) => {
            event.stopPropagation();
            if (jobs.length === 1) setJobInfoId(jobs[0].id);
            else setJobSelectorOpen(true);
          }}
        ><Search fontSize="inherit" /></IconButton> : null}
      </div>
      <div className="kgpboxwrap">
        {x.content
          .sort(
            (a, b) =>
              (a.type === "resourceOrder" ? "a" : "b").localeCompare(b.type === "resourceOrder" ? "a" : "b") ||
              (a.type === "resourceOrder" ? "" : a.resource.number).localeCompare(b.type === "resourceOrder" ? "" : b.resource.number),
          )
          .map((y) =>
            y.type === "resourceOrder" ? (
              <KGPResourceOrder date={date} key={y.type + y.id + "_" + y.key} y={y} />
            ) : search([y.resource.name, y.resource.number]) ? (
              <KGPResource
                transferred={transferredNumbers.has(y.resource.number)}
                disabled={disabled}
                parent={{
                  type: x.type,
                  id: x.type === "project" ? x.project.id : x.type === "specialPlace" ? x.id : null,
                }}
                y={y}
                key={y.type + y.id}
              />
            ) : null,
          )}
      </div>
      {crew !== null ? <div className={"kgpcrew" + (crew.length === 0 ? " nocrew" : "")}>{crew || "Keine Kolonne"}</div> : null}
      {jobSelectorOpen && jobs.length > 1 ? (
        <Dialog open={true} fullWidth maxWidth="md" onClose={() => setJobSelectorOpen(false)}>
          <DialogTitle>Bauphase wählen</DialogTitle>
          <DialogContent className="jobSelectorContainer">
            {jobs.map((job) => (
              <Button className="kgpjobSelector" key={job.id} onClick={() => {
                setJobSelectorOpen(false);
                setJobInfoId(job.id);
              }}>
                {job.phase.name || "Bauphase"} · Job {job.id}
              </Button>
            ))}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setJobSelectorOpen(false)}>Abbrechen</Button>
          </DialogActions>
        </Dialog>
      ) : null}
    </Paper>
  );
}
